import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Mail, Users, Database, FileText, CreditCard, Cable, ShieldAlert } from "lucide-react";
import { Container, Section, Eyebrow, DisplayHeading, BodyCopy, Button, Reveal, StatusBadge, Badge, TextLink } from "@/components/kit";
import { MaskedLinesInView } from "@/components/MaskedLines";
import SecurityTrace from "@/components/SecurityTrace";
import { CTASection } from "@/components/shared";
import { color } from "@/lib/theme";
import { fadeUp, stagger, viewport } from "@/lib/motion";

/* ---------------- Hero instrument ---------------- */
const TESTS = [
  { name: "Prompt Injection", result: "passed" },
  { name: "Sensitive Information Disclosure", result: "passed" },
  { name: "Jailbreak Resistance", result: "finding" },
  { name: "Insecure Output Handling", result: "passed" },
  { name: "Excessive Agency", result: "finding" },
  { name: "System Prompt Exposure", result: "passed" },
];

function SecurityInstrument() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const t = setInterval(() => setRevealed((r) => {
      if (r >= TESTS.length) { clearInterval(t); return r; }
      return r + 1;
    }), 500);
    return () => clearInterval(t);
  }, [inView]);
  const done = revealed >= TESTS.length;
  return (
    <div ref={ref} className="relative mx-auto w-full max-w-[540px]">
      <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-brand/[0.075] blur-3xl" />
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="relative overflow-hidden rounded-xl border border-line bg-panel-raised/70 p-5 backdrop-blur-xl sm:p-7"
      >
        <div className="absolute inset-0 tech-grid opacity-60" />
        <div className="relative flex flex-wrap items-start justify-between gap-2 border-b border-line pb-4 sm:gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">Meira Security Assessment</div>
            <div className="mt-1 font-mono text-[8.5px] uppercase tracking-[0.18em] text-brand-soft/80">Simulated environment</div>
          </div>
          <StatusBadge status={done ? "verified" : "active"}>{done ? "Complete" : "Running"}</StatusBadge>
        </div>

        <div className="relative mt-4 grid grid-cols-2 gap-2.5 sm:gap-3">
          <Meta k="AI System" v="Customer Support Agent" />
          <Meta k="Assessment" v="ASM-DEMO-0814-04" />
        </div>

        <div className="relative mt-4 flex flex-col divide-y divide-line-soft">
          {TESTS.map((t, i) => {
            const shown = i < revealed;
            const scanning = i === revealed;
            return (
              <div key={t.name} className="flex items-center justify-between gap-3 py-2.5" style={{ background: scanning ? color("brand", 0.06) : "transparent" }}>
                <span className={`min-w-0 flex-1 text-[12px] leading-snug transition-colors sm:text-[13px] ${shown ? "text-content" : "text-content-faint"}`}>{t.name}</span>
                {shown ? <StatusBadge status={t.result === "passed" ? "passed" : "finding"} className="shrink-0" /> : <span className="shrink-0 font-mono text-[10px] uppercase tracking-wide text-content-faint">{scanning ? "Testing…" : "Queued"}</span>}
              </div>
            );
          })}
        </div>

        <div className="relative mt-5 grid grid-cols-4 gap-2 border-t border-line pt-5">
          <Tally n="126" l="Tests" />
          <Tally n="2" l="Findings" tone="warn" />
          <Tally n="1" l="High" tone="danger" />
          <Tally n="1" l="Medium" tone="warn" />
        </div>
        <div className="relative mt-4 text-center font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">Demo assessment · simulated data</div>
      </motion.div>
    </div>
  );
}

function Meta({ k, v }) {
  return (
    <div className="rounded-md border border-line-soft bg-panel p-3">
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">{k}</div>
      <div className="mt-1 truncate text-[13px] text-content">{v}</div>
    </div>
  );
}
function Tally({ n, l, tone }) {
  const c = tone === "warn" ? "text-warn" : tone === "danger" ? "text-danger" : "text-content";
  return (
    <div className="text-center">
      <div className={`font-display text-2xl font-light ${c}`}>{n}</div>
      <div className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-content-faint">{l}</div>
    </div>
  );
}

/* ---------------- Attack surface ---------------- */
const SURFACES = [
  { label: "Instructions", attack: "Prompt injection" },
  { label: "Context", attack: "System prompt exposure" },
  { label: "Data", attack: "Sensitive disclosure" },
  { label: "Outputs", attack: "Insecure output" },
  { label: "Tools", attack: "Excessive agency" },
  { label: "Permissions", attack: "Tool misuse" },
];

const DOMAINS = [
  ["01", "Prompt & instruction attacks", "Prompt injection · instruction manipulation · jailbreak resistance"],
  ["02", "Data & information exposure", "Sensitive disclosure · context leakage · system prompt exposure"],
  ["03", "Output safety", "Insecure output handling · policy boundary failures"],
  ["04", "Agent & tool risk", "Excessive agency · tool misuse · permission boundary testing"],
  ["05", "Behavioral robustness", "Adversarial behavior · guardrail resilience"],
  ["06", "Control effectiveness", "Whether safeguards actually reduce the tested risk"],
];

function AttackSurface() {
  const [active, setActive] = useState(null);
  return (
    <div className="grid gap-8 sm:gap-10 lg:grid-cols-[1fr_1fr] lg:items-center">
      {/* central visual */}
      <div className="relative rounded-lg border border-line bg-panel-raised/50 p-5 tech-grid sm:p-8">
        <div className="mb-7 flex justify-center sm:mb-8">
          <div className="relative flex h-28 w-28 flex-col items-center justify-center rounded-full border-2 border-brand/50 bg-ink brand-glow">
            <span className="absolute inset-0 animate-pulse-ring rounded-full bg-brand/25" />
            <ShieldAlert size={30} strokeWidth={1.3} className="text-brand-soft" />
            <span className="mt-1 font-mono text-[8px] uppercase tracking-[0.16em] text-content-muted">AI System</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {SURFACES.map((s) => (
            <button
              key={s.label}
              data-testid={`surface-${s.label.toLowerCase()}`}
              onMouseEnter={() => setActive(s.label)}
              onFocus={() => setActive(s.label)}
              onMouseLeave={() => setActive(null)}
              onClick={() => setActive((a) => (a === s.label ? null : s.label))}
              aria-pressed={active === s.label}
              className={`rounded-md border p-3 text-left transition-colors duration-300 focus:outline-none ${active === s.label ? "border-warn/50 bg-warn/8" : "border-line bg-panel hover:border-brand/50"}`}
            >
              <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-content-faint">Surface</div>
              <div className="mt-0.5 text-[13px] font-medium text-content">{s.label}</div>
              <div className={`mt-1 font-mono text-[9px] uppercase tracking-wide transition-colors ${active === s.label ? "text-warn" : "text-content-faint"}`}>{s.attack}</div>
            </button>
          ))}
        </div>
      </div>

      {/* domains */}
      <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="flex flex-col divide-y divide-line-soft border-y border-line-soft">
        {DOMAINS.map(([n, t, d]) => (
          <motion.div key={n} variants={fadeUp} className="grid grid-cols-[auto_1fr] gap-4 py-4">
            <span className="font-mono text-[11px] text-brand-soft">{n}</span>
            <div>
              <div className="text-sm font-medium text-content">{t}</div>
              <div className="mt-1 text-[13px] leading-relaxed text-content-muted">{d}</div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}

/* ---------------- Lifecycle rail ---------------- */
const LIFECYCLE = [
  { id: "find", label: "Find", outputs: ["Finding", "Severity", "Affected system", "Risk mapping"] },
  { id: "fix", label: "Fix", outputs: ["Recommended remediation", "Mapped control", "Owner", "Evidence requirement"] },
  { id: "retest", label: "Retest", outputs: ["Previous finding", "Retest result", "Control effectiveness", "Residual risk"] },
  { id: "prove", label: "Prove", outputs: ["Evidence package", "Audit trail", "Assurance outcome", "Assurance record"] },
];

function LifecycleRail() {
  const [active, setActive] = useState("find");
  return (
    <div>
      <div className="relative flex flex-col gap-3 md:flex-row md:items-stretch md:gap-0">
        {LIFECYCLE.map((s, i) => {
          const isActive = active === s.id;
          return (
            <React.Fragment key={s.id}>
              <button
                data-testid={`lifecycle-${s.id}`}
                onMouseEnter={() => setActive(s.id)}
                onFocus={() => setActive(s.id)}
                onClick={() => setActive(s.id)}
                className={`group relative flex-1 rounded-lg border p-5 text-left transition-colors duration-300 focus:outline-none sm:p-6 ${isActive ? "border-brand/50 bg-brand/8" : "border-line bg-panel-raised hover:border-brand/50"}`}
              >
                <span className={`font-display text-4xl font-light ${isActive ? "text-brand-soft" : "text-content/12"}`}>0{i + 1}</span>
                <h3 className="mt-2 font-display text-xl text-content">{s.label}</h3>
                <motion.div initial={false} animate={{ height: isActive ? "auto" : 0, opacity: isActive ? 1 : 0 }} className="overflow-hidden">
                  <div className="mt-4 flex flex-col gap-1.5">
                    {s.outputs.map((o) => (
                      <div key={o} className="flex items-center gap-2 text-[13px] text-content-muted">
                        <span className="h-1 w-1 rounded-full bg-brand-soft" />{o}
                      </div>
                    ))}
                  </div>
                </motion.div>
              </button>
              {i < LIFECYCLE.length - 1 && <div className="hidden items-center px-2 text-content-faint md:flex">→</div>}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Agentic ---------------- */
const CONNECTORS = [
  { label: "Email", icon: Mail, perm: "Send", state: "allowed" },
  { label: "CRM", icon: Users, perm: "Read / Write", state: "allowed" },
  { label: "Database", icon: Database, perm: "Read", state: "scoped" },
  { label: "Files", icon: FileText, perm: "Read", state: "scoped" },
  { label: "Payments", icon: CreditCard, perm: "Blocked", state: "blocked" },
  { label: "APIs", icon: Cable, perm: "Scoped", state: "scoped" },
];

/* ---------------- Page ---------------- */
export default function AISecurity() {
  return (
    <>
      {/* 01 HERO */}
      <section className="relative overflow-hidden pb-12 pt-28 sm:pb-16 sm:pt-36 md:pb-24 md:pt-44">
        <div className="pointer-events-none absolute inset-0 tech-grid radial-fade opacity-50" />
        <div className="pointer-events-none absolute -top-20 left-1/4 h-[280px] w-[420px] -translate-x-1/2 rounded-full bg-brand/[0.06] blur-[95px] sm:-top-32 sm:h-[480px] sm:w-[820px] sm:blur-[130px]" />
        <Container className="relative">
          <div className="grid items-center [&>*]:min-w-0 gap-10 sm:gap-14 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <motion.div variants={fadeUp} initial="hidden" animate="show"><Eyebrow>AI Security</Eyebrow></motion.div>
              <h1 className="mt-6 font-display text-[32px] font-medium leading-[1.05] tracking-tight text-content sm:mt-7 sm:text-5xl md:text-6xl">
                <MaskedLinesInView lines={["Attack your AI", "before someone else does."]} />
              </h1>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3}>
                <BodyCopy className="mt-7 max-w-xl">
                  Stress-test AI systems in controlled test environments and turn vulnerabilities into
                  evidence-backed remediation.
                </BodyCopy>
                <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:flex-wrap sm:items-center">
                  <Button to="/contact" variant="primary" data-testid="aisec-book-demo" className="w-full max-w-xs uppercase tracking-[0.12em] sm:w-auto sm:max-w-none">Book a Demo</Button>
                  <div className="flex flex-col">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-brand">Controlled testing</span>
                    <span className="font-mono text-[10px] tracking-wide text-content-faint">Authorization required</span>
                  </div>
                </div>
              </motion.div>
            </div>
            <SecurityInstrument />
          </div>
        </Container>
      </section>

      {/* 02 PRINCIPLE */}
      <Section className="border-t border-line !py-16">
        <Container>
          <Reveal>
            <div className="relative overflow-hidden rounded-lg border border-brand/25 bg-gradient-to-r from-brand/8 to-transparent p-6 sm:p-8 md:p-10">
              <span className="absolute left-0 top-0 h-full w-[3px] bg-brand" />
              <div className="grid gap-6 md:grid-cols-[1fr_1.4fr] md:items-center">
                <div>
                  <Eyebrow tone="brand">Meira Security Principle</Eyebrow>
                  <DisplayHeading className="mt-4 text-2xl leading-tight sm:text-3xl">Controlled. Authorized. Evidence-based.</DisplayHeading>
                </div>
                <BodyCopy className="!text-[15px]">
                  Meira security testing runs only in controlled test environments and only with explicit
                  authorization. Meira does not attack customer production AI systems.
                </BodyCopy>
              </div>
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* 03 WHY DIFFERENT + FINDING */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <div className="grid gap-9 sm:gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <Reveal><Eyebrow>Why AI security is different</Eyebrow></Reveal>
              <DisplayHeading className="mt-6 text-3xl leading-tight sm:text-4xl">The attack surface speaks natural language.</DisplayHeading>
              <Reveal i={1}>
                <BodyCopy className="mt-6">
                  AI systems introduce attack surfaces traditional application security wasn’t designed to
                  evaluate. An attacker may manipulate instructions, expose sensitive context, bypass
                  safeguards, influence tool use, or push an agent beyond its intended authority.
                </BodyCopy>
                <BodyCopy className="mt-4">
                  Meira evaluates how AI systems behave under controlled adversarial pressure — then turns
                  findings into risks, controls and evidence. It complements traditional security by
                  addressing AI-specific behavioral attack surfaces.
                </BodyCopy>
              </Reveal>
            </div>
            <Reveal i={1}>
              <div className="relative overflow-hidden rounded-lg border border-warn/25 bg-panel-raised p-5 sm:p-7">
                <span className="absolute left-0 top-0 h-full w-[3px] bg-warn" />
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">Finding · FND-DEMO-0042</span>
                  <StatusBadge status="finding">High</StatusBadge>
                </div>
                <h4 className="mt-4 font-display text-xl text-content">Jailbreak Resistance</h4>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <Field k="Observation" v="Model followed a prohibited instruction pattern during a simulated adversarial assessment." />
                  <Field k="Impact" v="Guardrail bypass may enable behavior outside the intended operating policy." />
                  <Field k="Risk" v="Unauthorized model behavior" />
                  <Field k="Control" v="Input / Output Guardrail" />
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-line pt-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-warn">Remediation required</span>
                  <span className="font-mono text-[9px] uppercase tracking-wide text-content-faint">Simulated data</span>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* 04 ATTACK SURFACE */}
      <Section className="border-t border-line">
        <Container>
          <Reveal><Eyebrow>AI attack surface</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">Test the ways AI can actually fail.</DisplayHeading>
          <Reveal i={1}><BodyCopy className="mt-6 max-w-2xl">Meira maps its testing to recognized AI security guidance, including OWASP guidance relevant to LLM and GenAI security.</BodyCopy></Reveal>
          <Reveal i={1} className="mt-8 sm:mt-12"><AttackSurface /></Reveal>
        </Container>
      </Section>

      {/* 05 SIGNATURE: ATTACK → EVIDENCE */}
      <Section className="relative overflow-hidden border-t border-line bg-panel-raised/50">
        <div className="pointer-events-none absolute -right-40 top-1/4 hidden h-[520px] w-[520px] rounded-full bg-brand/[0.04] blur-[130px] sm:block" />
        <div className="pointer-events-none absolute -left-40 top-0 hidden h-[420px] w-[420px] rounded-full bg-brand/[0.05] blur-[130px] sm:block" />
        <div className="pointer-events-none absolute inset-0 tech-grid opacity-30" />
        <Container className="relative">
          <Reveal><Eyebrow tone="brand">Meira Security Trace</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 text-3xl leading-tight sm:text-4xl md:text-5xl">Don’t stop at the finding.</DisplayHeading>
          <Reveal i={1}><BodyCopy className="mt-6 max-w-2xl">Follow a security finding from adversarial test to governed, evidence-backed assurance. This is what makes Meira more than a scanner.</BodyCopy></Reveal>
          <Reveal i={1} className="mt-10 sm:mt-14"><SecurityTrace /></Reveal>
        </Container>
      </Section>

      {/* 06 LIFECYCLE */}
      <Section className="border-t border-line">
        <Container>
          <Reveal><Eyebrow>The operating model</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 text-3xl leading-tight sm:text-4xl">Find → Fix → Retest → Prove.</DisplayHeading>
          <Reveal i={1} className="mt-8 sm:mt-12"><LifecycleRail /></Reveal>
          <Reveal i={1}>
            <div className="mt-8 grid gap-5 rounded-lg border border-line bg-panel-raised/50 p-5 sm:p-7 md:grid-cols-[1fr_1fr] md:gap-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-soft">Current workflow</div>
                <p className="mt-2 text-[13px] leading-relaxed text-content-muted">Testing · findings · risk & control mapping · remediation workflow · retesting · evidence · assurance.</p>
                <div className="mt-3"><Badge tone="brand">Available</Badge></div>
              </div>
              <div className="md:border-l md:border-line md:pl-7">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">Autonomous loop</div>
                <p className="mt-2 text-[13px] leading-relaxed text-content-muted">Meira is evolving this toward increasingly autonomous red-team → remediation → retest → proof cycles.</p>
                <div className="mt-3 flex gap-2"><Badge tone="brand">In rollout</Badge><Badge tone="neutral">Planned</Badge></div>
              </div>
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* 07 AGENTIC */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <div className="grid gap-10 sm:gap-14 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div>
              <Reveal><Eyebrow>Agentic AI security</Eyebrow></Reveal>
              <DisplayHeading className="mt-6 text-3xl leading-tight sm:text-4xl">When AI can act, the blast radius changes.</DisplayHeading>
              <Reveal i={1}>
                <BodyCopy className="mt-6">
                  Chatbot risk focuses on output. Agentic systems can also access systems, read information,
                  modify records and call tools. So security must ask not only what the AI can say — but what
                  it can do.
                </BodyCopy>
                <p className="mt-4 font-mono text-[11px] leading-relaxed tracking-wide text-content-faint">
                  Meira tests, assesses and maps agent permissions in a controlled environment. Runtime
                  enforcement varies by integration.
                </p>
                <div className="mt-6"><TextLink to="/governance">Security doesn’t live alone — explore Governance</TextLink></div>
              </Reveal>
            </div>
            <Reveal i={1}>
              <div className="relative rounded-lg border border-line bg-panel-raised/60 p-5 tech-grid sm:p-8">
                <div className="mb-8 flex flex-col items-center">
                  <div className="relative flex h-24 w-24 flex-col items-center justify-center rounded-full border border-brand/40 bg-ink brand-glow">
                    <span className="absolute inset-0 animate-pulse-ring rounded-full bg-brand/25" />
                    <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-brand-soft">Demo Agent</span>
                  </div>
                  <span className="mt-2 font-mono text-[8.5px] uppercase tracking-[0.16em] text-content-faint">Meira test environment</span>
                </div>
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                  {CONNECTORS.map((c) => {
                    const tone = c.state === "blocked" ? "border-danger/30 bg-danger/5" : c.state === "scoped" ? "border-warn/25 bg-warn/5" : "border-line bg-panel";
                    const pc = c.state === "blocked" ? "text-danger" : c.state === "scoped" ? "text-warn" : "text-valid";
                    return (
                      <div key={c.label} className={`flex flex-col gap-1.5 rounded-md border p-3 ${tone}`}>
                        <c.icon size={16} strokeWidth={1.5} className="text-brand-soft" />
                        <span className="text-[12px] font-medium text-content">{c.label}</span>
                        <span className={`font-mono text-[9px] uppercase tracking-wide ${pc}`}>{c.state} · {c.perm}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 flex flex-wrap items-center justify-between gap-2 rounded-md border border-valid/25 bg-valid/5 px-4 py-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-content-muted">Test · unauthorized payment</span>
                  <StatusBadge status="passed">Control effective</StatusBadge>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      <CTASection eyebrow="Test your AI" title={["Turn vulnerabilities", "into verifiable proof."]} body="See how Meira turns AI security findings into governed remediation, retest evidence and assurance others can verify." />
    </>
  );
}

function Field({ k, v }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">{k}</div>
      <div className="mt-1 text-[13px] leading-relaxed text-content-muted">{v}</div>
    </div>
  );
}
